import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { contentService, type AppLanguage } from '@/services/contentService';
import { currentLangAtom } from '@/store/langAtom';

function byDisplayOrder(a: AppLanguage, b: AppLanguage): number {
  const ao = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
  const bo = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  return a.description.localeCompare(b.description);
}

export function useLanguages(): { languages: AppLanguage[]; isLoading: boolean } {
  const currentLang = useAtomValue(currentLangAtom);

  const { data: allLanguages = [], isLoading } = useQuery({
    queryKey: ['app-languages'],
    queryFn: contentService.getLanguages,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

  const languages = useMemo(() =>
    allLanguages
      .filter(l =>
        (l.activeFlag === 'Y' && l.ppVisibleFlag === 'Y') ||
        l.appLanguageCode === currentLang,
      )
      .slice()
      .sort(byDisplayOrder),
    [allLanguages, currentLang],
  );

  return { languages, isLoading };
}
